import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';

export default function Register() {
  const { register, login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) {
      setErr('Passwords do not match');
      return;
    }
    if (!acceptedTerms) {
      setErr('Please accept the Terms and Conditions to continue.');
      return;
    }
    setBusy(true);
    try {
      // register() in auth.jsx already calls /api/users/bootstrap which
      // creates the RTDB profile with credits=0. The second call was a
      // duplicate; keep the bootstrap in the auth layer as the single
      // source of truth.
      await register(email.trim(), password, fullName.trim());
      nav('/pricing');
    } catch (e) {
      const code = e && e.code ? String(e.code) : '';
      // If the email is already registered, don't dead-end with an error
      // message — try signing the user in with the same password. A returning
      // user who typed their real password ends up at the dashboard;
      // someone using the wrong password gets a nudge to the login page.
      if (code === 'auth/email-already-in-use') {
        try {
          await login(email.trim(), password);
          nav('/dashboard');
          return;
        } catch (signInErr) {
          const signInCode = signInErr && signInErr.code ? String(signInErr.code) : '';
          if (signInCode === 'auth/wrong-password' || signInCode === 'auth/invalid-credential'
              || signInCode === 'auth/invalid-login-credentials') {
            setErr(
              'An account with this email already exists. Please log in instead — ' +
              'or reset your password if you forgot it.',
            );
          } else {
            setErr(
              'An account with this email already exists. Please log in from the sign-in page.',
            );
          }
          return;
        }
      }
      setErr(e instanceof Error ? e.message : 'Registration failed');
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setErr(null);
    if (!acceptedTerms) {
      setErr('Please accept the Terms and Conditions to continue.');
      return;
    }
    setGoogleBusy(true);
    try {
      await loginWithGoogle();
      nav('/pricing');
    } catch (e) {
      const code = e && e.code ? String(e.code) : '';
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        setErr(e instanceof Error ? e.message : 'Google sign-up failed');
      }
    } finally {
      setGoogleBusy(false);
    }
  }

  return (
    <div className="auth-wrapper">
      <aside className="auth-panel">
        <div>
          <Link to="/" className="d-inline-flex align-items-center gap-2 text-decoration-none" style={{ color: '#fff' }}>
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                display: 'grid',
                placeItems: 'center',
                fontSize: '1rem',
                boxShadow: 'var(--shadow-brand)',
              }}
            >
              <i className="fas fa-graduation-cap"></i>
            </span>
            <span style={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Dissertation Island</span>
          </Link>
        </div>
        <div>
          <h2 style={{ fontWeight: 800, fontSize: '2.2rem', lineHeight: 1.15, color: '#fff' }}>
            Create an account.
            <br />
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: 'italic',
                fontWeight: 400,
                background: 'linear-gradient(135deg, #818cf8, #38bdf8)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Start drafting in minutes.
            </span>
          </h2>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              marginTop: '1.8rem',
              color: 'rgba(255,255,255,0.75)',
              fontSize: '0.95rem',
            }}
          >
            <PerkLine icon="fa-bolt" text="5-chapter dissertations, drafted in minutes" />
            <PerkLine icon="fa-chart-line" text="Real data analysis + figures" />
            <PerkLine icon="fa-file-export" text="PDF and Word export included" />
            <PerkLine icon="fa-credit-card" text="Pay per run — no subscription" />
          </ul>
        </div>
        <div
          className="d-flex align-items-center gap-3 small"
          style={{ color: 'rgba(255,255,255,0.6)' }}
        >
          <i className="fas fa-lock"></i>
          Your email is only used to sign you in
        </div>
      </aside>

      <div className="auth-form-side">
        <div className="auth-card fade-in-up">
          <div className="auth-icon">
            <i className="fas fa-user-plus"></i>
          </div>
          <div className="text-center mb-4">
            <h3 className="fw-bold mb-1">Create your account</h3>
            <p className="text-muted small mb-0">
              Already have one?{' '}
              <Link to="/login" className="fw-semibold">
                Log in
              </Link>
            </p>
          </div>

          {err && <div className="alert alert-danger">{err}</div>}

          <div
            className="form-check mb-3"
            style={{
              fontSize: '0.85rem',
              padding: '0.75rem 0.75rem 0.75rem 2.25rem',
              background: '#f8fafc',
              border: '1px solid var(--border)',
              borderRadius: 8,
            }}
          >
            <input
              type="checkbox"
              className="form-check-input"
              id="tcCheckbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
            <label className="form-check-label text-muted" htmlFor="tcCheckbox">
              I agree to the{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer">
                Terms and Conditions
              </Link>
              . I understand the output is a <strong>draft</strong> and that I am responsible
              for verifying citations, data, and editing before submission.
            </label>
          </div>

          <button
            type="button"
            className="btn btn-outline-secondary w-100 mb-3 d-flex align-items-center justify-content-center gap-2"
            onClick={onGoogle}
            disabled={googleBusy || busy || !acceptedTerms}
            style={{ padding: '0.6rem', fontWeight: 500 }}
            title={!acceptedTerms ? 'Accept the Terms and Conditions to continue' : undefined}
          >
            {googleBusy ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" />
                <span>Opening Google…</span>
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                </svg>
                <span>Sign up with Google</span>
              </>
            )}
          </button>

          <div className="d-flex align-items-center gap-3 my-3 text-muted small">
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span>or create with email</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          <form onSubmit={onSubmit}>
            <div className="mb-3">
              <label className="form-label">Full name</label>
              <input
                className="form-control"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
                required
                autoFocus
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  placeholder="6+ characters"
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Confirm</label>
                <input
                  type="password"
                  className="form-control"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={6}
                  placeholder="Repeat password"
                  required
                />
              </div>
            </div>
            <button type="submit" className="btn btn-cta w-100 py-2" disabled={busy || !acceptedTerms}>
              {busy ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Creating account…
                </>
              ) : (
                <>
                  Create account
                  <i className="fas fa-arrow-right ms-2"></i>
                </>
              )}
            </button>
          </form>
          <p className="text-muted small text-center mt-3 mb-0">
            After signup you'll pick a credit plan to run your first dissertation.
          </p>
        </div>
      </div>
    </div>
  );
}

function PerkLine({ icon, text }) {
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.7rem' }}>
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.12)',
          display: 'grid',
          placeItems: 'center',
          color: '#c7d2fe',
          fontSize: '0.85rem',
        }}
      >
        <i className={`fas ${icon}`}></i>
      </span>
      {text}
    </li>
  );
}
